import React, { useCallback, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { DatePickerProps } from "./types";
import { TextField } from "../TextField/TextField";
import { classNames } from "helpers";

const GRID_CELLS = 42; // 6 tjedana
const CALENDAR_MAX_HEIGHT = 360;
const CALENDAR_OFFSET = 8;

const DatePicker: React.FC<DatePickerProps> = (props) => {
  const {
    onChange,
    label,
    placeholder,
    name,
    error,
    required,
    width,
    cssClassName,
    value,
    showTime,
    minDate,
    disabled,
  } = props;

  const wrapperRef = useRef<HTMLDivElement>(null);
  const calendarRef = useRef<HTMLDivElement>(null);
  const currentLanguage = "hr";

  const [calendarStyle, setCalendarStyle] = useState<React.CSSProperties>({});

  const updateCalendarPosition = useCallback(() => {
    if (!wrapperRef.current) return;

    const rect = wrapperRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUp = spaceBelow < CALENDAR_MAX_HEIGHT && rect.top > spaceBelow;

    setCalendarStyle({
      position: "fixed",
      left: rect.left,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + CALENDAR_OFFSET }
        : { top: rect.bottom + CALENDAR_OFFSET }),
    });
  }, []);

  // Funkcija koja sigurno konvertira value u Date
  const toDate = (val: Date | string | null | undefined): Date | null => {
    if (!val) return null;
    if (val instanceof Date) return val;

    // Ako je string u formatu "dd.mm.yyyy"
    const parts = val.split?.(".");
    if (parts?.length === 3) {
      const [day, month, year] = parts.map(Number);
      return new Date(year, month - 1, day);
    }

    // Ako je ISO string
    const parsed = new Date(val);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // State
  const [showCalendar, setShowCalendar] = useState(false);
  const [calendarView, setCalendarView] = useState<"days" | "years">("days");
  const [selectedDate, setSelectedDate] = useState<Date | null>(toDate(value));
  const [currentYear, setCurrentYear] = useState<number>(
    toDate(value)?.getFullYear() ?? new Date().getFullYear()
  );
  const [currentMonth, setCurrentMonth] = useState<number>(
    toDate(value)?.getMonth() ?? new Date().getMonth()
  );
  const [yearRangeStart, setYearRangeStart] = useState<number>(
    (toDate(value)?.getFullYear() ?? new Date().getFullYear()) - 4
  );

  // Weekdays
  const weekDays = React.useMemo(() => {
    const baseDate = new Date(2021, 7, 2);
    return Array.from({ length: 7 }, (_, i) => {
      const date = new Date(baseDate);
      date.setDate(baseDate.getDate() + i);
      return date.toLocaleDateString(currentLanguage, { weekday: "short" });
    });
  }, [currentLanguage]);

  // Month label
  const monthLabel = new Date(currentYear, currentMonth).toLocaleDateString(
    currentLanguage,
    { month: "long", year: "numeric" }
  );

  // Klik izvan komponente zatvara kalendar. Kalendar je u portalu, pa se provjerava i on —
  // inače bi se zatvorio prije nego klik na dan uopće okine.
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;

      if (wrapperRef.current?.contains(target)) return;
      if (calendarRef.current?.contains(target)) return;

      setShowCalendar(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Kalendar se renderira u portalu jer ga scroll kontejneri (npr. ModalBody s
  // overflow-y-auto) inače režu; pozicija se računa iz okvira polja.
  useEffect(() => {
    if (!showCalendar) return;

    updateCalendarPosition();

    // Fixed pozicija ne prati scroll pretka — capture faza hvata scroll bilo kojeg kontejnera.
    window.addEventListener("scroll", updateCalendarPosition, true);
    window.addEventListener("resize", updateCalendarPosition);

    return () => {
      window.removeEventListener("scroll", updateCalendarPosition, true);
      window.removeEventListener("resize", updateCalendarPosition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCalendar]);

  // Sinkronizacija s value prop
  useEffect(() => {
    const date = toDate(value);
    setSelectedDate(date);
    if (date) {
      setCurrentYear(date.getFullYear());
      setCurrentMonth(date.getMonth());
    } else {
      const now = new Date();
      setCurrentYear(now.getFullYear());
      setCurrentMonth(now.getMonth());
    }
  }, [value]);

  // Mreža počinje ponedjeljkom prije 1. u mjesecu (zaglavlje je ponedjeljak-prvi) i puni se
  // danima susjednih mjeseci do punih 6 tjedana.
  const days = React.useMemo(() => {
    const offset = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7;

    return Array.from({ length: GRID_CELLS }, (_, i) => {
      const date = new Date(currentYear, currentMonth, i - offset + 1);
      return { date, isOutside: date.getMonth() !== currentMonth };
    });
  }, [currentYear, currentMonth]);

  const isDayDisabled = (date: Date): boolean => {
    if (!minDate) return false;
    const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    return new Date(date.getFullYear(), date.getMonth(), date.getDate()) < min;
  };

  const isDaySelected = (date: Date): boolean =>
    !!selectedDate &&
    selectedDate.getDate() === date.getDate() &&
    selectedDate.getMonth() === date.getMonth() &&
    selectedDate.getFullYear() === date.getFullYear();

  // Handleri
  const handleDateSelect = (date: Date) => {
    const hours = showTime && selectedDate ? selectedDate.getHours() : 0;
    const minutes = showTime && selectedDate ? selectedDate.getMinutes() : 0;
    const newDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
    setSelectedDate(newDate);
    if (!showTime) setShowCalendar(false);
    if (onChange) onChange(name, newDate);
  };

  const handleTimeChange = (type: "hours" | "minutes", val: string) => {
    const num = parseInt(val, 10);
    if (isNaN(num)) return;
    const base = selectedDate ?? new Date();
    const newDate = new Date(base);
    if (type === "hours" && num >= 0 && num <= 23) newDate.setHours(num);
    if (type === "minutes" && num >= 0 && num <= 59) newDate.setMinutes(num);
    setSelectedDate(newDate);
    if (onChange) onChange(name, newDate);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  const formatDate = (date: Date | null): string => {
    if (!date) return "";
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    if (showTime) {
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day}.${month}.${year} ${hours}:${minutes}`;
    }
    return `${day}.${month}.${year}`;
  };

  const parseDate = (value: string): Date | null => {
    if (showTime) {
      const [datePart, timePart] = value.split(" ");
      const [day, month, year] = (datePart ?? "").split(".").map(Number);
      const [hours, minutes] = (timePart ?? "0:0").split(":").map(Number);
      return year && month && day ? new Date(year, month - 1, day, hours || 0, minutes || 0) : null;
    }
    const [day, month, year] = value.split(".").map(Number);
    return year && month && day ? new Date(year, month - 1, day) : null;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = parseDate(e.target.value);
    if (date) {
      setSelectedDate(date);
      setCurrentYear(date.getFullYear());
      setCurrentMonth(date.getMonth());
      if (onChange) onChange(name, date);
    }
  };

  // If date is selected, don't show error (it will clear on next form validation)
  const displayError = selectedDate ? undefined : error;

  return (
    <div ref={wrapperRef} className="relative">
      <TextField
        label={label}
        type="text"
        name={name}
        value={formatDate(selectedDate)}
        onChange={disabled ? undefined : handleInputChange}
        onClick={disabled ? undefined : () => setShowCalendar(true)}
        placeholder={placeholder}
        error={displayError}
        required={required}
        width={width}
        cssClassName={classNames(cssClassName, disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer")}
        endIcon="/media/icons/calendar.svg"
        disabled={disabled}
      />

      {showCalendar &&
        createPortal(
        <div
          ref={calendarRef}
          style={calendarStyle}
          className="z-[60] w-80 min-w-80 max-w-80 p-4 bg-white rounded-sm ring-1 ring-slate-200 shadow-2xl"
        >
          {calendarView === "days" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={handlePrevMonth}
                  className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
                >
                  ‹
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setYearRangeStart(currentYear - 4);
                    setCalendarView("years");
                  }}
                  className="text-sm font-semibold text-slate-800 hover:text-sky-600 cursor-pointer capitalize"
                >
                  {monthLabel}
                </button>
                <button
                  type="button"
                  onClick={handleNextMonth}
                  className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
                >
                  ›
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {weekDays.map((day) => (
                  <div key={day} className="text-[11px] font-semibold text-slate-400 uppercase">
                    {day}
                  </div>
                ))}

                {days.map(({ date, isOutside }, index) => (
                  <button
                    key={index}
                    type="button"
                    disabled={isDayDisabled(date)}
                    onClick={() => handleDateSelect(date)}
                    className={classNames(
                      "h-8 text-sm rounded-sm",
                      isDayDisabled(date)
                        ? "text-slate-300 cursor-not-allowed"
                        : isDaySelected(date)
                        ? "bg-sky-600 text-white font-semibold"
                        : isOutside
                        ? "text-slate-300 hover:bg-sky-50"
                        : "text-slate-700 hover:bg-sky-50"
                    )}
                  >
                    {date.getDate()}
                  </button>
                ))}
              </div>
            </>
          )}

          {calendarView === "years" && (
            <>
              <div className="flex items-center justify-between mb-3">
                <button
                  type="button"
                  onClick={() => setYearRangeStart((prev) => prev - 12)}
                  className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
                >
                  ‹
                </button>
                <span className="text-sm font-semibold text-slate-800">{yearRangeStart} - {yearRangeStart + 11}</span>
                <button
                  type="button"
                  onClick={() => setYearRangeStart((prev) => prev + 12)}
                  className="w-7 h-7 flex items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
                >
                  ›
                </button>
              </div>

              <div className="grid grid-cols-3 gap-2 text-center text-sm">
                {Array.from({ length: 12 }, (_, i) => yearRangeStart + i).map((year) => (
                  <button
                    key={year}
                    type="button"
                    onClick={() => {
                      setCurrentYear(year);
                      setCalendarView("days");
                    }}
                    className={`h-9 text-sm rounded-sm ${
                      year === currentYear
                        ? "bg-sky-600 text-white font-semibold"
                        : "text-slate-700 hover:bg-sky-50"
                    }`}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </>
          )}

          {showTime && (
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-200">
              <span className="text-sm text-slate-500">Vrijeme</span>
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={23}
                  value={selectedDate ? String(selectedDate.getHours()).padStart(2, "0") : "00"}
                  onChange={(e) => handleTimeChange("hours", e.target.value)}
                  className="w-12 px-2 py-1 text-sm text-center border border-slate-300 rounded"
                />
                <span className="text-sm font-bold">:</span>
                <input
                  type="number"
                  min={0}
                  max={59}
                  value={selectedDate ? String(selectedDate.getMinutes()).padStart(2, "0") : "00"}
                  onChange={(e) => handleTimeChange("minutes", e.target.value)}
                  className="w-12 px-2 py-1 text-sm text-center border border-slate-300 rounded"
                />
              </div>
              <button
                type="button"
                onClick={() => setShowCalendar(false)}
                className="px-3 py-1 text-sm text-white bg-sky-600 rounded-sm hover:bg-sky-700"
              >
                OK
              </button>
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePicker;
